/**
 * ShopDomainModal — replaces the legacy `window.prompt` on the
 * Integrations page Continue-setup CTA (Plan-ShopifyIntegrationsCleanup.md §C).
 *
 * Collects the customer's Shopify store domain and kicks off the OAuth
 * flow via `shopifyAPI.getAuthUrl(shop)`. The auth-URL helper redirects
 * via `window.location.href` on success, so the modal stays mounted with
 * its loading spinner until the navigation actually happens.
 *
 * Normalization mirrors the backend: trim → lowercase → strip
 * `http(s)://` → strip trailing slash. The backend also accepts shops
 * without a `.myshopify.com` suffix, so we don't require it client-side.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, ShoppingBag, X } from 'lucide-react';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (shop: string) => Promise<void> | void;
    /** Pre-fill the shop input — used by the Reconnect-store button on
     *  ShopifyStatusCard so the merchant can confirm/edit before kicking
     *  Flow A OAuth. Defaults to empty string (legacy Continue-setup flow). */
    initialShop?: string;
    /** Optional title/subtitle/CTA overrides so the same modal can serve
     *  both the Continue-setup flow and the Reconnect-store flow. */
    title?: string;
    subtitle?: string;
    submitLabel?: string;
    submittingLabel?: string;
}

function normalizeShopDomain(raw: string): string {
    return raw
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/\/+$/, '');
}

const ShopDomainModal: React.FC<Props> = ({
    isOpen,
    onClose,
    onSubmit,
    initialShop = '',
    title = 'Connect Shopify Store',
    subtitle = 'Enter your store domain to begin OAuth.',
    submitLabel = 'Connect',
    submittingLabel = 'Connecting...',
}) => {
    const [shop, setShop] = useState(initialShop);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    // Reset state every time the modal opens, and autofocus the input.
    useEffect(() => {
        if (isOpen) {
            setShop(initialShop);
            setError(null);
            setSubmitting(false);
            // Defer focus to after the modal paints so the input is mounted.
            const id = window.setTimeout(() => inputRef.current?.focus(), 0);
            return () => window.clearTimeout(id);
        }
    }, [isOpen, initialShop]);

    // ESC closes (unless mid-submit, where the redirect is about to fire).
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !submitting) {
                onClose();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, submitting, onClose]);

    // Focus trap — keep Tab navigation inside the dialog.
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key !== 'Tab' || !dialogRef.current) return;
            const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
                'input, button, [href], [tabindex]:not([tabindex="-1"])',
            );
            if (focusables.length === 0) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement as HTMLElement | null;
            if (e.shiftKey && active === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen]);

    if (!isOpen) return null;

    const trimmed = shop.trim();
    const disabled = submitting || trimmed.length === 0;

    const handleSubmit = async (e?: React.FormEvent) => {
        e?.preventDefault();
        const normalized = normalizeShopDomain(shop);
        if (!normalized) {
            setError('Please enter your Shopify store domain.');
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            await onSubmit(normalized);
            // On success, the caller redirects via window.location — we
            // stay mounted with the spinner so the user sees activity until
            // the navigation actually fires. If the caller resolves without
            // redirecting (rare), drop the spinner.
        } catch (err: unknown) {
            const msg =
                (err as { response?: { data?: { detail?: string } } })
                    ?.response?.data?.detail ||
                'Failed to start Shopify connection. Please try again.';
            setError(msg);
            setSubmitting(false);
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            onClick={() => {
                if (!submitting) onClose();
            }}
            aria-hidden="false"
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="shop-domain-modal-title"
                className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-200 dark:border-gray-700"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 px-5 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center shrink-0">
                            <ShoppingBag className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div className="min-w-0">
                            <h2
                                id="shop-domain-modal-title"
                                className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100"
                            >
                                {title}
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {subtitle}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => !submitting && onClose()}
                        disabled={submitting}
                        className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} className="px-5 sm:px-6 py-5 space-y-4">
                    <div>
                        <label
                            htmlFor="shop-domain-input"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                        >
                            Shopify store domain
                        </label>
                        <input
                            ref={inputRef}
                            id="shop-domain-input"
                            type="text"
                            value={shop}
                            onChange={(e) => {
                                setShop(e.target.value);
                                if (error) setError(null);
                            }}
                            placeholder="acme.myshopify.com"
                            disabled={submitting}
                            autoComplete="off"
                            autoCapitalize="off"
                            autoCorrect="off"
                            spellCheck={false}
                            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed text-sm font-mono"
                            aria-invalid={!!error}
                            aria-describedby={error ? 'shop-domain-error' : undefined}
                        />
                        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                            With or without <code className="px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-800 font-mono">.myshopify.com</code> works.
                        </p>
                        {error && (
                            <p
                                id="shop-domain-error"
                                role="alert"
                                className="mt-2 text-xs text-red-600 dark:text-red-400"
                            >
                                {error}
                            </p>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-1">
                        <button
                            type="button"
                            onClick={() => !submitting && onClose()}
                            disabled={submitting}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={disabled}
                            className="btn-primary flex items-center justify-center gap-1.5 px-4 py-2 text-sm w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {submittingLabel}
                                </>
                            ) : (
                                <>
                                    <ShoppingBag className="w-4 h-4" />
                                    {submitLabel}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body,
    );
};

export default ShopDomainModal;
